import { useState } from "react";
import { Ruler, Undo2, Trash2, X, Check, RotateCcw, RotateCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { pathM, vallasFor, fmtM, VALLA_M } from "@/lib/geo";

const Box = ({ children, testid, tone = "sky" }) => (
  <div className={`absolute bottom-16 left-1/2 -translate-x-1/2 z-[600] bg-[#090f20]/95 backdrop-blur-md border rounded-xl p-3 w-[320px] rise ${tone === "amber" ? "border-amber-400/40" : "border-sky-500/40"}`} data-testid={testid}>
    {children}
  </div>
);

export const MeasurePanel = ({ pts, onUndo, onClear, onClose }) => {
  const m = pathM(pts);
  return (
    <Box testid="measure-panel">
      <div className="flex items-center gap-2 text-xs text-sky-300 font-semibold"><Ruler className="w-4 h-4" /> Regla · pulse puntos en el mapa</div>
      <div className="flex items-end gap-6 mt-2">
        <div><div className="font-mono text-[10px] text-slate-500">DISTANCIA</div><div className="font-display text-2xl font-bold text-white" data-testid="measure-distance">{fmtM(m)}</div></div>
        <div><div className="font-mono text-[10px] text-slate-500">VALLAS ({VALLA_M} m)</div><div className="font-display text-2xl font-bold text-amber-300" data-testid="measure-vallas">{vallasFor(m)}</div></div>
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={onUndo} disabled={!pts.length} className="px-2 py-1.5 rounded-md bg-white/10 text-xs disabled:opacity-40" data-testid="measure-undo-button"><Undo2 className="w-3.5 h-3.5" /></button>
        <button onClick={onClear} disabled={!pts.length} className="px-2 py-1.5 rounded-md bg-white/10 text-xs disabled:opacity-40" data-testid="measure-clear-button"><Trash2 className="w-3.5 h-3.5" /></button>
        <button onClick={onClose} className="ml-auto flex items-center gap-1 px-3 py-1.5 rounded-md bg-white/10 text-xs" data-testid="measure-close-button"><X className="w-3.5 h-3.5" /> Cerrar regla</button>
      </div>
    </Box>
  );
};

export const BoxSavePanel = ({ corners, count, onSave, onCancel }) => {
  const [name, setName] = useState("");
  return (
    <Box testid="box-template-panel" tone="amber">
      <div className="text-xs text-amber-300 font-semibold">Plantilla de señalización</div>
      <div className="text-xs text-slate-400 mt-1">
        {corners.length < 2 ? (corners.length ? "Pulse la esquina opuesta del recuadro." : "Pulse una esquina del recuadro que contiene las señales.") : `${count} objeto(s) dentro del recuadro.`}
      </div>
      {corners.length === 2 && (
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre (p. ej. Cruce en T con desvío)" className="h-8 text-xs mt-2" data-testid="box-template-name-input" />
      )}
      <div className="flex gap-2 mt-3">
        <button onClick={() => onSave(name.trim())} disabled={corners.length < 2 || !count || !name.trim()} data-testid="box-template-save-button"
          className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-amber-400 text-slate-950 text-xs font-semibold disabled:opacity-40"><Check className="w-3.5 h-3.5" /> Guardar plantilla</button>
        <button onClick={onCancel} className="px-3 py-1.5 rounded-md bg-white/10 text-xs" data-testid="box-template-cancel-button">Cancelar</button>
      </div>
    </Box>
  );
};

export const GroupPlacePanel = ({ tpl, placed, rot, setRot, onDone, onCancel }) => (
  <Box testid="group-place-panel" tone="amber">
    <div className="text-xs text-amber-300 font-semibold">{tpl.name}</div>
    <div className="text-xs text-slate-400 mt-1">{placed ? "Gire el conjunto para ajustarlo a la calle. Puede pulsar otro punto para moverlo." : "Pulse en el mapa el cruce donde colocar la plantilla."}</div>
    {placed && (
      <div className="flex items-center gap-2 mt-3" data-testid="group-rotation">
        <button onClick={() => setRot((rot + 345) % 360)} className="p-1.5 rounded-md bg-white/5 hover:bg-white/10" data-testid="group-rotate-left"><RotateCcw className="w-4 h-4" /></button>
        <Slider value={[rot]} min={0} max={359} step={5} onValueChange={([v]) => setRot(v)} className="flex-1" data-testid="group-rotation-slider" />
        <button onClick={() => setRot((rot + 15) % 360)} className="p-1.5 rounded-md bg-white/5 hover:bg-white/10" data-testid="group-rotate-right"><RotateCw className="w-4 h-4" /></button>
        <span className="font-mono text-xs text-slate-300 w-10 text-right">{rot}°</span>
      </div>
    )}
    <div className="flex gap-2 mt-3">
      <button onClick={onDone} disabled={!placed} className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-500 text-slate-950 text-xs font-semibold disabled:opacity-40" data-testid="group-place-done-button"><Check className="w-3.5 h-3.5" /> Hecho</button>
      <button onClick={onCancel} className="px-3 py-1.5 rounded-md bg-white/10 text-xs" data-testid="group-place-cancel-button">Cancelar</button>
    </div>
  </Box>
);
